import { type ReactNode, useState } from "react"

import { MonitoringStatus } from "@/components/terminal/monitoring-status"
import { PillSelect } from "@/components/terminal/pill-select"
import { TooltipProvider } from "@/components/ui/tooltip"
import { buildFileSections } from "@/lib/terminal/company-file"
import type { TerminalFileView } from "@/lib/terminal/file-view"

import { ActivityBox } from "./activity-box"
import { AssessmentLegend } from "./assessment-legend"
import { FileBrief } from "./file-brief"
import { FileDataMasonry } from "./file-data-masonry"
import { FileIndividualsSection } from "./file-individuals-section"
import { FileDetailSection } from "./file-section"
import { MastheadAssessment } from "./masthead-assessment"
import { StartMonitoring } from "./start-monitoring"

const isPeopleSectionShown = false

// The pill that reveals every File box; each other pill narrows to one box.
const SHOW_ALL_SECTION = "all"

// The File pane's scroll container and content column, shared by the loaded
// File and the route's pending and error states so the masthead stays put
// across the load handoff.
export const FilePane = ({ children }: { children: ReactNode }) => (
  <div className="@container/company-detail-pane h-full overflow-y-auto">
    <div className="@container/company-detail flex w-full flex-col gap-(--terminal-space-block) p-6 pb-12 @3xl/company-detail-pane:p-8 @5xl/company-detail-pane:p-12">
      {children}
    </div>
  </div>
)

// One Company's Delivered File, as a buyer reads it, from TerminalFileView.
// The route owns loading, errors and the monitoring server call.
export const TerminalFilePage = ({
  file,
  onStartMonitoring,
}: {
  file: TerminalFileView
  onStartMonitoring: (approvedOn: string | null) => Promise<void>
}) => {
  const [isLegendOpen, setIsLegendOpen] = useState(false)
  const [activeSection, setActiveSection] = useState(SHOW_ALL_SECTION)
  const isMonitoring = file.portfolioEntry.isMonitoring
  // A monitored Company's Activity leads the File boxes; a one-time Company
  // has none.
  const boxes = [
    ...(isMonitoring
      ? [
          {
            id: "activity",
            title: "Reassessments",
            node: (
              <ActivityBox
                history={file.history}
                detections={file.detections}
              />
            ),
          },
        ]
      : []),
    ...buildFileSections(file).map((section) => ({
      id: section.id,
      title: section.title,
      node: <FileDetailSection section={section} />,
    })),
  ]

  return (
    <TooltipProvider delayDuration={300}>
      {/* The legend is a fixed overlay, kept outside the animated body: a
          transformed ancestor would clip and re-anchor it. */}
      {isLegendOpen && (
        <AssessmentLegend
          currentBand={file.assessment.band}
          onClose={() => setIsLegendOpen(false)}
        />
      )}
      <FilePane>
        <FileMasthead
          company={file.company}
          assessment={file.assessment}
          isMonitoring={isMonitoring}
          onAssessmentClick={() => setIsLegendOpen(true)}
          onStartMonitoring={onStartMonitoring}
        />

        {/* One rhythm down the page, from the spacing tokens: the block gap
            between the masthead, the Brief, the chips and each box; the box
            lead from the chips to the first box, whose label straddles its
            top edge. */}
        <div className="animate-in fade-in slide-in-from-bottom-1 flex flex-col gap-(--terminal-space-block) duration-(--duration-base)">
          <div className="flex flex-col gap-(--terminal-space-block)">
            {/* A Company has no Brief until its first one is written. */}
            {file.brief && <FileBrief brief={file.brief} />}

            {boxes.length > 0 && (
              <div className="flex flex-col gap-(--terminal-space-box-lead)">
                <PillSelect
                  mode="single"
                  ariaLabel="Filter File sections"
                  value={
                    boxes.some(({ id }) => id === activeSection)
                      ? activeSection
                      : SHOW_ALL_SECTION
                  }
                  onChange={setActiveSection}
                  options={[
                    { id: SHOW_ALL_SECTION, label: "Show All" },
                    ...boxes.map(({ id, title }) => ({ id, label: title })),
                  ]}
                />
                <FileDataMasonry
                  items={boxes
                    .filter(
                      ({ id }) =>
                        activeSection === SHOW_ALL_SECTION ||
                        id === activeSection
                    )
                    .map(({ id, node }) => ({ key: id, node }))}
                />
              </div>
            )}
          </div>

          {/* People is held back from the Night Shift release (Dhanur,
              24 Oct 2026); it returns when it is designed on its own. */}
          {isPeopleSectionShown && (
            <FileIndividualsSection individuals={file.individuals} />
          )}
        </div>
      </FilePane>
    </TooltipProvider>
  )
}

// The File masthead: name, the current score's square and band word (its
// move shows only in Reassessments), the UNCLEAR reason, Monitoring or Start monitoring, and
// its rule. The route paints it from the Portfolio row while the File loads,
// with an inert assessment.
export const FileMasthead = ({
  company,
  assessment,
  isMonitoring,
  onAssessmentClick,
  onStartMonitoring,
}: {
  company: TerminalFileView["company"]
  assessment: TerminalFileView["assessment"]
  isMonitoring: boolean
  onAssessmentClick?: () => void
  onStartMonitoring: (approvedOn: string | null) => Promise<void>
}) => (
  <header>
    <h1 className="type-display break-words">{company.name}</h1>
    <div className="mt-3 flex flex-wrap items-center gap-4">
      {onAssessmentClick ? (
        <button
          type="button"
          onClick={onAssessmentClick}
          aria-haspopup="dialog"
          className="inline-flex cursor-pointer border-0 bg-transparent p-0"
        >
          <MastheadAssessment assessment={assessment} />
        </button>
      ) : (
        <MastheadAssessment assessment={assessment} />
      )}
      {isMonitoring ? (
        <MonitoringStatus />
      ) : (
        <StartMonitoring
          companyName={company.name}
          assessment={assessment}
          onStartMonitoring={onStartMonitoring}
        />
      )}
    </div>
    <div className="border-border-strong mt-3.5 border-t" />
  </header>
)
